import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Eye, ChefHat, Check, Printer, Download, Trash2, BellRing } from 'lucide-react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';


const statuses = [
  { key: 'pending', label: 'En attente', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
  { key: 'preparing', label: 'En preparation', color: 'bg-[#1E2D3D]/10 text-[#1E2D3D]' },
  { key: 'ready', label: 'Prete', color: 'bg-[#5B6B3F]/15 text-[#5B6B3F]' },
  { key: 'served', label: 'Servie', color: 'bg-[#5B6B3F]/10 text-[#5B6B3F]' },
  { key: 'rejected', label: 'Refusee', color: 'bg-[#8C3A3A]/10 text-[#8C3A3A]' },
];
const getStatus = (key) => statuses.find(s => s.key === key) || statuses[0];
const typeLabels = { delivery: 'Livraison', takeaway: 'A emporter', dine_in: 'Sur place' };
const paymentMethodLabels = {
  cash: 'Argent liquide',
  card: 'Carte bleue en ligne',
  ticket_restaurant: 'Ticket restaurant',
  other: 'Carte bancaire a la reception',
  card_on_receipt: 'Carte bancaire a la reception',
};

const formatOrderType = (type) => typeLabels[type] || type || 'Commande';
const formatPaymentMethod = (order) => {
  if (!order?.payment_method) return 'Non precise';
  return paymentMethodLabels[order.payment_method] || order.payment_method;
};
const getVatBreakdown = (order) => {
  const defaultVatRate = Number(order?.default_vat_rate ?? 10);
  const buckets = {};
  (order?.items || []).forEach((item) => {
    const quantity = Number(item?.quantity || 0);
    const unitPrice = Number(item?.price || 0);
    const lineTotal = quantity * unitPrice;
    const rate = Number(item?.vat_rate ?? defaultVatRate);
    const vatAmount = lineTotal > 0 ? lineTotal - (lineTotal / (1 + (rate / 100))) : 0;
    const totalHt = lineTotal - vatAmount;
    if (!buckets[rate]) {
      buckets[rate] = { rate, totalTtc: 0, totalHt: 0, vatAmount: 0 };
    }
    buckets[rate].totalTtc += lineTotal;
    buckets[rate].totalHt += totalHt;
    buckets[rate].vatAmount += vatAmount;
  });

  return Object.values(buckets)
    .sort((a, b) => a.rate - b.rate)
    .map((line) => ({
      rate: line.rate,
      totalTtc: Number(line.totalTtc.toFixed(2)),
      totalHt: Number(line.totalHt.toFixed(2)),
      vatAmount: Number(line.vatAmount.toFixed(2)),
    }));
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [historyActionLoading, setHistoryActionLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (typeFilter) params.set('order_type', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    api.get(`/orders?${params}`).then(r => setOrders(r.data)).catch(() => {});
  }, [typeFilter, statusFilter]);
  useEffect(load, [load]);

  useEffect(() => {
    const handleNotification = (event) => {
      if (event.detail?.type === 'order') {
        load();
      }
    };
    window.addEventListener('admin-notification', handleNotification);
    return () => window.removeEventListener('admin-notification', handleNotification);
  }, [load]);

  const updateStatus = async (id, status, readyInMinutes = null) => {
    try {
      const response = await api.put(`/orders/${id}/status`, { status, ...(readyInMinutes !== null ? { ready_in_minutes: readyInMinutes } : {}) });
      // Stop this request alert for every successful admin action, including a
      // simultaneous confirmation that the server has already applied.
      if (["preparing", "rejected"].includes(status)) {
        window.dispatchEvent(new CustomEvent('admin-request-resolved', {
          detail: { type: 'order', referenceKey: 'order_id', referenceId: id },
        }));
      }
      toast.success('Statut mis a jour');
      load(); setSelected(null);
    } catch { toast.error('Erreur'); }
  };

  const confirmOrder = (order) => {
    if (order.pickup_preference === 'asap') {
      const defaultDelay = order.type === 'delivery' ? 30 : 10;
      const answer = window.prompt('Dans combien de minutes cette commande sera-t-elle prete ?', String(defaultDelay));
      if (answer === null) return;
      const minutes = Number(answer);
      if (!Number.isInteger(minutes) || minutes < 1 || minutes > 240) {
        toast.error('Indiquez un delai entier compris entre 1 et 240 minutes.');
        return;
      }
      updateStatus(order.id, 'preparing', minutes);
      return;
    }
    updateStatus(order.id, 'preparing');
  };

  const printOrder = async (order) => {
    if (!order?.id) return;
    setPrinting(true);
    try {
      await api.post('/printers/jobs', { order_id: order.id });
      toast.success("Ticket envoye a l'imprimante Epson");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur d'impression. Verifiez l'agent local et l'imprimante.");
    } finally {
      setPrinting(false);
    }
  };

  const downloadBlob = (data, filename, contentType) => {
    const blob = new Blob([data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadAndDeleteHistory = async () => {
    if (!window.confirm("L'historique des commandes terminées sera d'abord téléchargé puis supprimé. Continuer ?")) {
      return;
    }

    setHistoryActionLoading(true);
    try {
      const exportResponse = await api.get('/orders/history/export', { responseType: 'blob' });
      const disposition = exportResponse.headers['content-disposition'] || '';
      const fileNameMatch = disposition.match(/filename="?([^"]+)"?/);
      const fileName = fileNameMatch?.[1] || 'historique-commandes.xlsx';
      downloadBlob(exportResponse.data, fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

      const deleteResponse = await api.delete('/orders/history');
      toast.success(`Historique téléchargé puis supprimé (${deleteResponse.data?.deleted_count || 0} commande(s))`);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors du téléchargement ou de la suppression de l'historique");
    } finally {
      setHistoryActionLoading(false);
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-heading text-2xl font-bold text-[#2A2A28]">Commandes</h1>
        <Button
          type="button"
          variant="outline"
          disabled={historyActionLoading}
          onClick={downloadAndDeleteHistory}
          className="rounded-xl border-[#8C3A3A]/20 text-[#8C3A3A] hover:bg-[#8C3A3A]/5"
          data-testid="orders-download-delete-history"
        >
          {historyActionLoading ? <Download className="mr-2 h-4 w-4 animate-pulse" /> : <Trash2 className="mr-2 h-4 w-4" />}
          {historyActionLoading ? 'Traitement...' : "Télécharger puis supprimer l'historique"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <select className="h-9 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="orders-type-filter">
          <option value="">Tous types</option>
          {Object.entries(typeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="h-9 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="orders-status-filter">
          <option value="">Tous statuts</option>
          {statuses.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {orders.map(order => {
          const st = getStatus(order.status);
          return (
            <div key={order.id} className="bg-white rounded-xl border border-[#EBE8E0] p-4" data-testid={`order-${order.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-[#2A2A28]">{order.customer?.name || 'Client'}</h3>
                    <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
                    <Badge variant="outline" className="text-xs">{typeLabels[order.type]}</Badge>
                    {order.table_number > 0 && <Badge variant="outline" className="text-xs">Table {order.table_number}</Badge>}
                  </div>
                  <p className="text-sm text-[#7A7A75]">{order.items?.length || 0} articles - {order.total?.toFixed(2)} EUR</p>
                  <p className="text-xs text-[#7A7A75]">Paiement: {order.payment_status === 'paid' ? 'Paye' : 'En attente'}</p>
                  {order.payment_method && (
                    <p className="text-xs text-[#7A7A75]">Mode choisi: {paymentMethodLabels[order.payment_method] || order.payment_method}</p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0 flex-wrap">
                  <Button size="sm" variant="outline" className="rounded-lg border-[#EBE8E0]" onClick={() => setSelected(order)} data-testid={`view-order-${order.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Detail
                  </Button>
                  {order.status === 'pending' && <Button size="sm" className="rounded-lg bg-[#5B6B3F] hover:bg-[#4A5832] text-white" onClick={() => confirmOrder(order)} data-testid={`confirm-order-${order.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Confirmer</Button>}
                  {order.status === 'pending' && <Button size="sm" variant="outline" className="rounded-lg border-[#8C3A3A]/20 text-[#8C3A3A]" onClick={() => updateStatus(order.id, 'rejected')} data-testid={`reject-order-${order.id}`}>Refuser</Button>}
                  {order.status === 'preparing' && <>
                    <Button size="sm" className="rounded-lg bg-[#5B6B3F] hover:bg-[#4A5832] text-white" onClick={() => updateStatus(order.id, 'ready')}><BellRing className="w-3.5 h-3.5 mr-1" /> Prete</Button>
                    <Button size="sm" className="rounded-lg bg-[#1E2D3D] hover:bg-[#16212D] text-white" onClick={() => updateStatus(order.id, 'served')}><ChefHat className="w-3.5 h-3.5 mr-1" /> Servie</Button>
                  </>}
                  {order.status === 'ready' && <Button size="sm" className="rounded-lg bg-[#1E2D3D] hover:bg-[#16212D] text-white" onClick={() => updateStatus(order.id, 'served')}><ChefHat className="w-3.5 h-3.5 mr-1" /> Servie</Button>}
                </div>
              </div>
            </div>
          );
        })}
        {orders.length === 0 && <p className="text-center text-[#7A7A75] py-8">Aucune commande</p>}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader className="print:hidden">
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="font-heading">Detail de la commande</DialogTitle>
              <Button type="button" size="sm" className="rounded-lg bg-[#1E2D3D] hover:bg-[#16212D] text-white" onClick={() => printOrder(selected)} disabled={printing}>
                <Printer className="w-3.5 h-3.5 mr-1" /> Imprimer
              </Button>
            </div>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              {(() => {
                const subtotal = Number(selected.subtotal ?? selected.total ?? 0);
                const deliveryFee = Number(selected.delivery_fee || 0);
                const cardProcessingFee = Number(selected.card_processing_fee || 0);
                const total = Number(selected.total || 0);
                const vatBreakdown = getVatBreakdown(selected);
                const vatAmount = vatBreakdown.reduce((sum, line) => sum + Number(line.vatAmount || 0), 0);
                const totalExcludingVat = subtotal - vatAmount;
                return (
              <div className="print-receipt mx-auto w-full max-w-[320px] rounded-xl border border-dashed border-[#D7D2C7] bg-[#FFFDF8] p-4 text-[#111111] print:max-w-none print:border-0 print:bg-white print:p-0">
                <div className="mb-4 border-b border-dashed border-[#D7D2C7] pb-3 text-center">
                  <p className="text-base font-semibold tracking-[0.18em] uppercase">Le Traiteur Libanais</p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#6B665E]">Ticket commande</p>
                </div>

                <div className="space-y-1 text-[13px] leading-5">
                  <div className="flex justify-between gap-3">
                    <span className="font-medium">Commande</span>
                    <span className="text-right">{selected.id?.slice(0, 8)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="font-medium">Type</span>
                    <span className="text-right">{formatOrderType(selected.type)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="font-medium">Statut</span>
                    <span className="text-right">{getStatus(selected.status).label}</span>
                  </div>
                  {selected.created_at && (
                    <div className="flex justify-between gap-3">
                      <span className="font-medium">Recu le</span>
                      <span className="text-right">{new Date(selected.created_at).toLocaleString('fr-FR')}</span>
                    </div>
                  )}
                  {selected.pickup_time && (
                    <div className="flex justify-between gap-3">
                      <span className="font-medium">Heure</span>
                      <span className="text-right">{selected.pickup_time}</span>
                    </div>
                  )}
                  {selected.table_number > 0 && (
                    <div className="flex justify-between gap-3">
                      <span className="font-medium">Table</span>
                      <span className="text-right">{selected.table_number}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 border-t border-dashed border-[#D7D2C7] pt-3 text-[13px] leading-5">
                  <p><span className="font-medium">Client:</span> {selected.customer?.name || 'Client'}</p>
                  {selected.customer?.phone && <p><span className="font-medium">Tel:</span> {selected.customer.phone}</p>}
                  {selected.customer?.address && <p><span className="font-medium">Adresse:</span> {selected.customer.address}</p>}
                  <p><span className="font-medium">Paiement:</span> {formatPaymentMethod(selected)}</p>
                </div>

                <div className="mt-4 border-t border-dashed border-[#D7D2C7] pt-3">
                  <div className="mb-2 flex justify-between text-[12px] font-semibold uppercase tracking-[0.16em] text-[#6B665E]">
                    <span>Articles</span>
                    <span>Montant</span>
                  </div>
                  {selected.items?.map((item, i) => (
                    <div key={i} className="py-1.5 text-[13px]">
                      <div className="flex justify-between gap-3">
                        <span className="min-w-0">{item.quantity}x {item.name}</span>
                        <span className="shrink-0">{(item.price * item.quantity).toFixed(2)} EUR</span>
                      </div>
                      {(item.formula_selections || []).length > 0 && (
                        <div className="mt-1 space-y-1 pl-3 text-[11px] leading-4 text-[#6B665E]">
                          {item.formula_selections.map((group, groupIndex) => (
                            <p key={groupIndex}>
                              <span className="font-medium text-[#111111]">{group.group_name}:</span>{' '}
                              {group.items?.map(option => option.name).join(', ')}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-4 border-t border-dashed border-[#D7D2C7] pt-3 text-[13px] leading-5">
                  <div className="flex justify-between">
                    <span className="font-medium">Sous-total</span>
                    <span>{subtotal.toFixed(2)} EUR</span>
                  </div>
                  {deliveryFee > 0 && (
                    <div className="flex justify-between">
                      <span className="font-medium">Livraison</span>
                      <span>{deliveryFee.toFixed(2)} EUR</span>
                    </div>
                  )}
                  {cardProcessingFee > 0 && (
                    <div className="flex justify-between">
                      <span className="font-medium">Frais CB</span>
                      <span>{cardProcessingFee.toFixed(2)} EUR</span>
                    </div>
                  )}
                  {vatBreakdown.map((line) => (
                    <div key={`vat-${line.rate}`} className="flex justify-between">
                      <span className="font-medium">TVA incluse ({line.rate.toFixed(2)}%)</span>
                      <span>{line.vatAmount.toFixed(2)} EUR</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-[12px] text-[#6B665E]">
                    <span>Total HT</span>
                    <span>{totalExcludingVat.toFixed(2)} EUR</span>
                  </div>
                  <div className="mt-2 flex justify-between text-[15px] font-semibold">
                    <span>Total TTC</span><span>{total.toFixed(2)} EUR</span>
                  </div>
                </div>

                <div className="mt-4 border-t border-dashed border-[#D7D2C7] pt-3 text-center text-[11px] leading-4 text-[#6B665E]">
                  {vatBreakdown.map((line) => (
                    <p key={`vat-footer-${line.rate}`}>TVA appliquee au taux de {line.rate.toFixed(2)}%</p>
                  ))}
                  <p>Impression ticket compatible Epson TM-M30II</p>
                  <p>Le Traiteur Libanais</p>
                </div>
              </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
